<template>
  <div class="container py-3">
    <h2 class="mb-3">Admin Portfolios</h2>
    <div class="d-flex gap-2 align-items-center mb-3">
      <CompanySearchDropdown
        v-model="selectedCompanyId"
        placeholder="Select company"
        :with-label="false"
        :with-description="false"
      />
    </div>

    <div v-if="!selectedCompanyId" class="text-muted">
      Select a company to view portfolios.
    </div>

    <div v-else>
      <div v-if="isLoading" class="text-muted">Loading…</div>
      <div v-else>
        <PortfolioAccordion
          v-for="group in groupedByPortfolio"
          :key="group.portfolio_id"
          :portfolio-id="group.portfolio_id"
          :portfolio-name="group.portfolio_name"
          :portfolio-type="group.portfolio_type"
          :last-updated="group.updated_at"
          :volume-mwh="group.volume_mwh"
          :rows="group.rows"
          v-model="selections[group.portfolio_id]"
          :target-portfolios="portfolios as PortfolioWithInstructions[]"
          :disabled-actions="isWorking"
          @action:move="(toId) => handleMove(group.portfolio_id, toId)"
          @action:allocate="handleAllocate()"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import PortfolioAccordion from "@/admin/portfolios/components/PortfolioAccordion.vue";
import CompanySearchDropdown from "@/components/common/CompanySearchDropdown.vue";
import {
  useAsyncTask,
  type AsyncTaskStartResponse,
} from "@/composables/useAsyncTask";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import type {
  FlattenedPortfolioItem,
  MoveResult,
  PortfolioWithInstructions,
} from "@/types";
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

const route = useRoute();
const router = useRouter();

const selectedCompanyId = ref<number | null>(null);
// Removed search for MVP
const isLoading = ref(false);
const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();
const items = ref<FlattenedPortfolioItem[]>([]);
const allPortfolios = ref<PortfolioWithInstructions[]>([]);
const selections = reactive<Record<number, number[]>>({}); // portfolio_id -> selected item ids
const moveTask = useAsyncTask<MoveResult>();
const isAllocating = ref(false);

// A move holds the actions until its post-completion reload has landed, so this spans every
// non-idle state rather than only the in-flight ones.
const isWorking = computed(
  () => isAllocating.value || moveTask.state.value !== "idle"
);

function syncFromRoute() {
  const cid = route.query.company_id as string | undefined;
  selectedCompanyId.value = cid ? Number(cid) : null;
}

function syncToRoute() {
  const mergedQuery = { ...route.query };
  if (selectedCompanyId.value) {
    mergedQuery.company_id = String(selectedCompanyId.value);
  } else {
    delete mergedQuery.company_id;
  }
  router.replace({ query: mergedQuery });
}

async function loadAllPortfolios() {
  if (!selectedCompanyId.value) {
    allPortfolios.value = [];
    return;
  }
  try {
    const { data } = await goSolidApi.get(`/api/admin/portfolios`, {
      params: { company_id: selectedCompanyId.value },
    });
    allPortfolios.value = data.portfolios ?? [];
  } catch (e: any) {
    globalErrorHandler(e);
  }
}

async function loadItems() {
  if (!selectedCompanyId.value) {
    items.value = [];
    return;
  }
  isLoading.value = true;
  try {
    const { data } = await goSolidApi.get(
      `/api/admin/portfolios/flattened-portfolio-items`,
      {
        params: {
          company_id: selectedCompanyId.value,
          portfolio_types: allPortfolios.value.map((p) => p.portfolio_type),
          unit_price: 100,
          include_non_transferable: true,
          include_test_companies: true,
        },
      }
    );
    items.value = data.portfolio_items ?? [];
  } catch (e: any) {
    globalErrorHandler(e);
  } finally {
    isLoading.value = false;
  }
}

async function loadAllData() {
  await loadAllPortfolios();
  await loadItems();
  reconcileSelections();
}

const groupedByPortfolio = computed(() => {
  // Group items by portfolio_id
  const itemsMap = new Map<number, FlattenedPortfolioItem[]>();
  for (const it of items.value) {
    const pid = it.portfolio_id;
    const existing = itemsMap.get(pid);
    if (existing) {
      existing.push(it);
    } else {
      itemsMap.set(pid, [it]);
    }
  }

  // Filter portfolios to only those with valid numeric IDs and create portfolio groups
  return allPortfolios.value
    .filter(
      (portfolio) => typeof portfolio.id === "number" && portfolio.id != null
    )
    .map((portfolio) => {
      const portfolioId = portfolio.id as number;
      const rows = itemsMap.get(portfolioId) ?? [];
      return {
        portfolio_id: portfolioId,
        portfolio_name: portfolio.name,
        portfolio_type: portfolio.portfolio_type,
        updated_at:
          rows.length > 0
            ? rows.reduce(
                (max: string | null, r: FlattenedPortfolioItem) =>
                  !max || r.updated_at > max ? r.updated_at : max,
                null
              )
            : null,
        volume_mwh: rows.reduce(
          (sum: number, r: FlattenedPortfolioItem) => sum + (r.count ?? 0),
          0
        ),
        rows,
      };
    });
});

// Use allPortfolios directly for target selection
const portfolios = computed(() => allPortfolios.value);

function reconcileSelections() {
  // Get valid portfolio IDs
  const validPortfolioIds = new Set(
    allPortfolios.value
      .filter(
        (portfolio) => typeof portfolio.id === "number" && portfolio.id != null
      )
      .map((portfolio) => portfolio.id as number)
  );

  // Remove stale keys (portfolios that no longer exist)
  Object.keys(selections).forEach((key) => {
    const portfolioId = Number(key);
    if (!validPortfolioIds.has(portfolioId)) {
      delete selections[portfolioId];
    }
  });

  // Add missing keys (ensure each valid portfolio has an array)
  validPortfolioIds.forEach((portfolioId) => {
    if (!selections[portfolioId]) {
      selections[portfolioId] = [];
    }
  });
}

onMounted(() => {
  syncFromRoute();
});

watch([selectedCompanyId], () => {
  syncToRoute();
  loadAllData();
});

watch(
  [allPortfolios],
  () => {
    reconcileSelections();
  },
  { deep: true }
);

watch(moveTask.state, async (state) => {
  if (state === "success") {
    const movedCount = moveTask.result.value?.moved ?? 0;
    globalSuccessHandler(`Move completed (${movedCount} item(s))`, "Success");
    // Reset only once the reload has landed, so the actions stay disabled until the rows on screen
    // reflect the move — re-enabling them mid-reload invites a second move against stale ids.
    await loadAllData();
    moveTask.reset();
  } else if (state === "failed") {
    // Prefer the raw error: an axios failure carries the backend's error code, which the
    // "Failed to start task" fallback message does not.
    globalErrorHandler(
      moveTask.error.value ??
        new Error(moveTask.errorMessage.value ?? "Move failed")
    );
    moveTask.reset();
  }
});

function getSelectionFor(pid: number): number[] {
  return selections[pid] ?? [];
}

async function handleMove(pid: number, toPortfolioId: number) {
  if (!selectedCompanyId.value) return;
  const selectedIds = getSelectionFor(pid);
  if (selectedIds.length === 0) return;

  const from_portfolio_id = pid;
  const payload: Record<string, number | number[]> = {
    company_id: selectedCompanyId.value,
    from_portfolio_id,
    to_portfolio_id: toPortfolioId,
    item_ids: selectedIds,
  };
  // The move runs as a backend task; both outcomes are handled by the moveTask.state watcher.
  await moveTask.execute(async () => {
    const response = await goSolidApi.post<AsyncTaskStartResponse>(
      `/api/admin/portfolios/move-items`,
      payload
    );
    return response.data;
  });
}

async function handleAllocate() {
  if (!selectedCompanyId.value) return;
  try {
    isAllocating.value = true;
    await goSolidApi.post(`/api/admin/allocation/trigger`, {
      start_from_company_id: selectedCompanyId.value,
      only_one_company: true,
    });
    globalSuccessHandler("Allocation started", "Success");
  } catch (e: any) {
    globalErrorHandler(e);
  } finally {
    isAllocating.value = false;
  }
}
</script>

<style scoped lang="scss"></style>
